import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function LogisticsSupportPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-ship-line',
      title: 'Sea Freight',
      description: 'Cost-effective ocean freight solutions for bulk cargo with FCL and LCL options to major ports worldwide'
    },
    {
      icon: 'ri-plane-line',
      title: 'Air Freight',
      description: 'Fast and reliable air cargo services for time-sensitive shipments to international destinations'
    },
    {
      icon: 'ri-truck-line',
      title: 'Land Transport',
      description: 'Efficient domestic transportation from farm to port with refrigerated container options'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Door-to-Door Delivery',
      description: 'Complete end-to-end logistics from your warehouse to customer destination anywhere in the world'
    },
    {
      icon: 'ri-box-3-line',
      title: 'Warehousing',
      description: 'Temperature-controlled storage facilities for agricultural products with inventory management'
    },
    {
      icon: 'ri-route-line',
      title: 'Route Optimization',
      description: 'Strategic route planning to minimize transit time and costs while ensuring product freshness'
    }
  ];

  const features = [
    {
      title: 'Real-Time Tracking',
      description: 'Track your shipment at every stage with our advanced GPS and online tracking system',
      icon: 'ri-map-pin-time-line'
    },
    {
      title: 'Temperature Control',
      description: 'Refrigerated containers and climate-controlled transport for perishable goods',
      icon: 'ri-temp-cold-line'
    },
    {
      title: 'Insurance Coverage',
      description: 'Comprehensive cargo insurance protecting your goods throughout the journey',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Expert Handling',
      description: 'Professional loading, unloading, and handling by trained logistics specialists',
      icon: 'ri-hand-heart-line'
    }
  ];

  const partners = [
    'Maersk Line',
    'MSC Mediterranean Shipping',
    'CMA CGM Group',
    'DHL Global Forwarding',
    'FedEx Trade Networks',
    'UPS Supply Chain'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/b857441a8282eff6e67d45b8b6cd5de0.jpeg" alt="Horizon Pathways Logo" className="w-12 h-12 object-contain" />
              <span className="text-xl font-semibold text-gray-900">Horizon Pathways</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Home</Link>
              <Link to="/products" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Products</Link>
              <a href="#services" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Services</a>
              <a href="#about" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">About</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Contact</a>
            </div>
            <button className="px-6 py-2 border-2 border-emerald-600 text-emerald-600 rounded-full text-sm font-medium transition-all hover:bg-emerald-600 hover:text-white whitespace-nowrap">Get Quote</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-8 relative overflow-hidden" style={{ backgroundColor: '#F8F6F3' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
            <i className="ri-global-line text-emerald-700"></i>
            <span className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Logistics Support Services</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Seamless Global <br />
            <span className="italic font-serif text-emerald-700">Logistics Solutions</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">
            Reliable shipping and logistics services connecting your products to markets worldwide. From sea freight to air cargo, we handle every aspect of international transportation with precision and care.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button className="px-8 py-4 bg-emerald-600 text-white rounded-full text-base font-semibold hover:bg-emerald-700 transition-all whitespace-nowrap">
              Get Shipping Quote
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button className="px-8 py-4 border-2 border-gray-900 text-gray-900 rounded-full text-base font-semibold hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap">
              Track Shipment
              <i className="ri-map-pin-line ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">LOGISTICS SERVICES</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Comprehensive Transportation Solutions</h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">Multiple shipping options tailored to your cargo requirements and delivery timelines</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white hover:shadow-xl transition-all duration-300 group border border-gray-100">
                <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-all">
                  <i className={`${service.icon} text-3xl text-emerald-600 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-emerald-400 tracking-widest uppercase mb-4">WHY CHOOSE US</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Advanced Logistics Features</h2>
            <p className="text-gray-400 text-base max-w-2xl mx-auto">Industry-leading technology and services for safe and efficient transportation</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-emerald-600/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${feature.icon} text-4xl text-emerald-400`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">HOW IT WORKS</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Simple Logistics Process</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">We make international shipping hassle-free with our streamlined logistics management process from booking to delivery.</p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-emerald-600">01</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Booking &amp; Planning</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Share shipment details and we'll plan the optimal route and mode of transportation</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-emerald-600">02</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Pickup &amp; Loading</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Professional handling and loading of cargo with proper packaging and container selection</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-emerald-600">03</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Transportation &amp; Tracking</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Real-time tracking throughout the journey with regular status updates</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-emerald-600">04</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Delivery &amp; Confirmation</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Safe delivery to the destination with proof of delivery documentation</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img src="https://public.readdy.ai/ai/img_res/53739ed3a27ed661f91fe968c1ed7614.jpg" alt="Logistics Operations" className="w-full h-[600px] object-cover object-top rounded-3xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">TRUSTED PARTNERS</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Global Shipping Network</h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">Partnered with leading international carriers for reliable worldwide delivery</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 flex items-center justify-center shadow-sm hover:shadow-md transition-all">
                <span className="text-base font-semibold text-gray-700">{partner}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-8" style={{ backgroundColor: '#14B8A6' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Ship Your Products Worldwide?</h2>
          <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">Get competitive shipping rates and reliable logistics support for your export business. Request a quote today.</p>
          <button className="px-10 py-4 bg-white text-emerald-600 rounded-full text-base font-semibold hover:bg-gray-100 transition-all whitespace-nowrap">
            Get Shipping Quote
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
